import React, { useContext } from 'react';
import { ReplaceTokens, useTranslations } from '@vp/om-translations';
import {
  Box,
  Card,
  Column,
  FlexBox,
  GridContainer,
  H3,
  H4,
  Row,
  Span,
  Typography,
  useScreenClass,
} from '@vp/swan';
import { ModTarget } from '@vp/mod-father';
import { useFeature } from '@vp/core-ui-components';
import DetailsItemUxModWrapper from '../../../uxMods/orderDetails/DetailsItemUxModWrapper';
import useSiteData from '../../../hooks/useSiteData';
import {
  isItemInInitial,
  mapItemStatesToStatuses,
  shouldShowCmrd,
} from '../../../util/mapStateToStatus';
import useIdentityContext from '../../../hooks/useIdentityContext';
import { OrderDetailsContext } from '../../contexts/OrderDetailsContext';
import { LineItemActionsContextProvider } from '../../contexts/LineItemActionsContext';
import LineItemStatus from '../../shared/LineItemStatus';
import DateComponent from '../../shared/Date';
import { ItemSelectedOptions } from './ItemSelectedOptions';
import { MemoizedItemPreviewDisplay } from '../../shared/itemPreviewDisplay/ItemPreviewDisplay';
import { ItemTotalSummary } from './ItemTotalSummary';
import { SourcePageContext } from '../../contexts/SourcePageContext';
import { LineItemActionOverride } from '../../LineItemActionOverride';
import { useLineItemData } from '../../contexts/LineItemDataProvider';
import { ModWrapperWithPadding } from '../../../uxMods/ModWrapperWithPadding';
import { LineItemActions } from '../../shared/LineItemActions';
import { LineItemReorderActionStatusContextProvider } from '../../contexts/LineItemReorderActionStatusContext';
import TrackingDisplayLink from '../../shared/trackingDisplay/TrackingDisplayLink';
import { OrderActionsContext } from '../../contexts/OrderActionsContext';
import { useCustomerFeedbackGroup } from '../../contexts/CustomerFeedbackGroupsProvider';

import { FeedbackType } from '../../../types/feedback/Feedback';
import { FeedbackGroupState } from '../../../types/feedback/FeedbackGroup';

/**
 * A representation of a full lineItem for display in the orderDetails page.
 * {@link DetailsItemUxModWrapper} for any additional decoration
 * @returns {JSX.Element}
 * @constructor
 */
export const DetailsLineItemDisplay = () => {
  const screenClass = useScreenClass();
  const isMobileDisplay = screenClass === 'xs';
  const siteData = useSiteData();
  const [lineItemMessages] = useTranslations(['lineItemMessages']);
  const { order } = useContext(OrderDetailsContext);

  const item = useLineItemData();
  const {
    trackingData: generalTrackingData,
    states: lineItemStates,
    deliverability: { itemDeliverable: isDeliverable },
    cmrd: promisedArrivalDate,
    actions: { reorder: isReorderable },
    editDocumentUrl,
    productName,
    quantity,
    id,
    productKey,
    productVersion,
    livePreviewUrl,
    documentUrl: documentRefUrl,
    attributes: productAttributeList,
    isGroupItem,
  } = item;
  const { isItemSelfCancellable } = useContext(OrderActionsContext);
  const isSelfCancellable = isItemSelfCancellable(id);

  const lineItemStatuses = mapItemStatesToStatuses(
    lineItemStates,
    isDeliverable
  );
  const hidePastCMRDs = false;
  const showExpectedDelivery = shouldShowCmrd(
    promisedArrivalDate,
    lineItemStatuses,
    hidePastCMRDs
  );

  const { identity } = useIdentityContext();
  const { isAnonymousUser } = identity;
  const { debugPOSTEnabled } = useContext(LineItemActionOverride);
  const { isConfirmationPage: isConfirmation } = useContext(SourcePageContext);

  const isMigratedOrder = order.migrated;

  // TODO - all of these "show x button" consts make this file bloated. Once we have a shared item model between OD & OH we should refactor. Perhaps have a file "LineItemActionDeterminations"?

  // Always allow reorder for any deliverable product.
  // If a legacy product is not reorderable (does not have a new platform mapping) we still render
  // the button and pop a modal / fire a metric upon reorder attempt.
  // See {@link https://gitlab.com/vistaprint-org/order-management/orders/orderdetailsui/-/merge_requests/502}
  const reorderActionEnabled =
    siteData.reorderEnabled?.orderDetails &&
    !isConfirmation &&
    !isAnonymousUser &&
    isDeliverable;

  // Only show buyNow button if all reorder criteria is met, and feature is enabled
  // We still show buyNow button for items with `isReorderable: false`, because doc conversion may succeed on the reorder attempt
  const buyNowActionEnabled =
    useFeature('enableExpressCheckoutOnOrderDetails') && reorderActionEnabled;

  // Only allow edit design and reorder option if reorder is enabled and the order is editable.
  // This means the item must be reorderable for migrated orders, or contain an edit document URL for new platform orders
  const editAndReorderActionEnabled =
    reorderActionEnabled &&
    (isMigratedOrder ? isReorderable : editDocumentUrl !== null);

  // Since MCP currently does not allow post-order design edits we only allow them before payment capture (i.e. item in initial state)
  // Even though this is true, the link can be hidden by the product gating useEffect that runs when POST is initially rendered.
  const editDesignActionEnabled =
    debugPOSTEnabled ||
    (siteData?.postOrderStudioEnabled &&
      !isMigratedOrder &&
      !isAnonymousUser &&
      isItemInInitial(item));

  // only show tracking links if trackingLink data exists
  const showTrackingLinks = !!generalTrackingData;

  // Enable design review on the item if it has an active customer feedback request
  // specific to document review and in a pending state
  const { customerFeedbackGroupExists: designFeedbackActionEnabled } =
    useCustomerFeedbackGroup({
      lineItemId: id,
      requestType: FeedbackType.DOCUMENT_REVIEW,
      groupState: FeedbackGroupState.AWAITING_FEEDBACK,
    });

  return (
    <LineItemActionsContextProvider
      cancelActionEnabled={isSelfCancellable}
      editDesignActionEnabled={editDesignActionEnabled}
      reorderActionEnabled={reorderActionEnabled}
      editAndReorderActionEnabled={editAndReorderActionEnabled}
      designFeedbackActionEnabled={designFeedbackActionEnabled}
      buyNowActionEnabled={buyNowActionEnabled}
      isGroupItem={isGroupItem}
    >
      <DetailsItemUxModWrapper>
        <LineItemReorderActionStatusContextProvider>
          <Box aria-labelledby={`product-name-${id}`}>
            <Card p={6}>
              <GridContainer>
                <Row>
                  <Column span={4} spanXs={4} pr={7}>
                    {/* This visually hidden product name is intended for those using assistive tech 
                  because assistive tech expects all info described by a heading to follow it.
                  This product name is hidden from visual users to prevent redundancy since we also render 
                  a visible product name next to the product image */}
                    {isGroupItem ? (
                      // If this item is part of a group, its group name is already serving as an H3, so we render its product name as an H4
                      <H4
                        visuallyHidden
                        id={`product-name-${id}`}
                        data-testid="visually-hidden-product-name"
                      >
                        {productName}
                      </H4>
                    ) : (
                      // If this item is NOT part of a group, its product name will serve as an H3
                      <H3
                        visuallyHidden
                        id={`product-name-${id}`}
                        data-testid="visually-hidden-product-name"
                      >
                        {productName}
                      </H3>
                    )}

                    <MemoizedItemPreviewDisplay
                      productAttributeList={productAttributeList}
                      documentRefUrl={documentRefUrl}
                      livePreviewUrl={livePreviewUrl}
                      productKey={productKey}
                      productVersion={productVersion}
                      productName={productName}
                    />
                  </Column>
                  <Column span={8} spanXs={8}>
                    {/* This aria hidden product name is intended for those viewing the visual UI
                  and is hidden from assistive tech to prevent redundancy 
                  since we also render a visually hidden product name above the product image */}
                    {productName && (
                      <Typography
                        fontWeight="bold"
                        fontSize="standard"
                        aria-hidden
                        data-testid="aria-hidden-product-name"
                      >
                        {productName}
                      </Typography>
                    )}
                    <LineItemStatus statuses={lineItemStatuses} />
                    <ModTarget
                      hostModWrapper={ModWrapperWithPadding}
                      name="itemInfo"
                    />
                    {showTrackingLinks && (
                      <TrackingDisplayLink
                        trackingData={generalTrackingData}
                        productName={productName}
                        itemId={id}
                      />
                    )}
                    {showExpectedDelivery && (
                      <Typography
                        textColor="subtle"
                        data-testid="expected-delivery-date"
                      >
                        <ReplaceTokens
                          tokenizedString={
                            lineItemMessages.expectedDeliveryMessage
                          }
                          substitutions={{
                            date: <DateComponent date={promisedArrivalDate} />,
                          }}
                        />
                      </Typography>
                    )}
                    <div data-testid="item-quantity">
                      <Span textColor="subtle">
                        {`${lineItemMessages.quantityField}: `} {quantity}
                      </Span>
                    </div>
                  </Column>
                </Row>
                <Row>
                  <Column span={12}>
                    {/* only render selectedOptions for new platform orders */}
                    {isMigratedOrder ? <Box mt={6} /> : <ItemSelectedOptions />}
                    <ModTarget name="belowSelectedOptions" />
                  </Column>
                </Row>
                <FlexBox flexDirection="column" gap="between-actions">
                  <Row>
                    <Column span={12}>
                      <ItemTotalSummary />
                    </Column>
                  </Row>
                  <Row>
                    <Column span={12} className="noprint">
                      <LineItemActions />
                    </Column>
                  </Row>
                  <Row>
                    <Column
                      span={12}
                      mt={isMobileDisplay ? 4 : 0}
                      className="noprint"
                    >
                      <ModTarget name="itemDetails" />
                    </Column>
                  </Row>
                </FlexBox>
              </GridContainer>
            </Card>
          </Box>
        </LineItemReorderActionStatusContextProvider>
      </DetailsItemUxModWrapper>
    </LineItemActionsContextProvider>
  );
};
